/**
 * UI Event Context
 *
 * Manages canvas state driven by UI events from agent tool calls.
 * Provides dispatch function to process events and update the canvas.
 */

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/ui/Toast';
import { spaceApi } from '../services/api';
import type { UIEvent, CanvasState, Artifact } from '../types/ui-events';

interface UIEventContextValue {
  canvasState: CanvasState;
  dispatchUIEvent: (event: UIEvent) => void;
}

const UIEventContext = createContext<UIEventContextValue | null>(null);

type CanvasAction =
  | { type: 'show_entity'; entityType: CanvasState['activeEntity'] extends infer T ? T extends null ? never : T : never }
  | { type: 'show_artifact'; artifact: Artifact }
  | { type: 'artifact_loading' }
  | { type: 'artifact_error'; error: string }
  | { type: 'highlight_field'; highlight: { entityType: string; entityId: string; field: string } }
  | { type: 'clear_canvas' };

const initialState: CanvasState = {
  activeEntity: null,
  activeArtifact: null,
  highlights: [],
  artifactLoading: false,
  artifactError: undefined,
};

function canvasReducer(state: CanvasState, action: CanvasAction): CanvasState {
  switch (action.type) {
    case 'show_entity':
      return {
        activeEntity: action.entityType,
        activeArtifact: null,
        highlights: [],
        artifactLoading: false,
        artifactError: undefined,
      };
    case 'show_artifact':
      return {
        activeEntity: null,
        activeArtifact: action.artifact,
        highlights: [],
        artifactLoading: false,
        artifactError: undefined,
      };
    case 'artifact_loading':
      return {
        activeEntity: null,
        activeArtifact: null,
        highlights: [],
        artifactLoading: true,
        artifactError: undefined,
      };
    case 'artifact_error':
      return {
        activeEntity: null,
        activeArtifact: null,
        highlights: [],
        artifactLoading: false,
        artifactError: action.error,
      };
    case 'highlight_field':
      return {
        ...state,
        highlights: [...state.highlights, action.highlight],
      };
    case 'clear_canvas':
      return initialState;
    default:
      return state;
  }
}

interface UIEventProviderProps {
  children: ReactNode;
  /** Owning space for any artifact lookups dispatched through the canvas.
   *  Required for `show_artifact` to resolve content (artifacts live under
   *  `/spaces/{spaceId}/artifacts/{id}`). */
  spaceId?: string;
}

export function UIEventProvider({ children, spaceId }: UIEventProviderProps) {
  const [canvasState, dispatch] = useReducer(canvasReducer, initialState);
  const queryClient = useQueryClient();
  const toast = useToast();

  const dispatchUIEvent = useCallback(
    (event: UIEvent) => {
      switch (event.eventType) {
        case 'show_entity': {
          const { entityType, entityId, section } = event.data as {
            entityType: 'agent' | 'skill' | 'tool' | 'document';
            entityId: string;
            section?: string;
          };
          dispatch({
            type: 'show_entity',
            entityType: { entityType, entityId, section },
          });
          // Invalidate queries so entity data refreshes
          queryClient.invalidateQueries({ queryKey: [entityType + 's', entityId] });
          queryClient.invalidateQueries({ queryKey: [entityType + 's'] });
          break;
        }
        case 'highlight_field': {
          const { entityType, entityId, field } = event.data as {
            entityType: string;
            entityId: string;
            field: string;
          };
          dispatch({
            type: 'highlight_field',
            highlight: { entityType, entityId, field },
          });
          break;
        }
        case 'show_artifact': {
          const data = event.data as Record<string, unknown>;
          if (import.meta.env.DEV) console.log('[artifact-dispatch] show_artifact received:', {
            hasContentType: !!data.contentType,
            hasContent: !!data.content,
            hasUrl: !!data.url,
            hasArtifactId: !!data.artifactId,
            keys: Object.keys(data),
          });

          if (data.contentType && (data.content || data.url)) {
            // Enriched path: adapter already resolved artifact from cache
            if (import.meta.env.DEV) console.log('[artifact-dispatch] show_artifact: using enriched path');
            dispatch({ type: 'show_artifact', artifact: data as unknown as Artifact });
          } else if (data.artifactId && typeof data.artifactId === 'string') {
            // Async fallback: only have artifactId, fetch from API
            if (import.meta.env.DEV) console.log('[artifact-dispatch] show_artifact: fetching from API for', data.artifactId);
            dispatch({ type: 'artifact_loading' });
            (spaceId ? spaceApi(spaceId).artifacts.get(data.artifactId) : Promise.reject(new Error('spaceId required to load artifact')))
              .then((ref) => {
                if (!ref || !ref.contentType) {
                  dispatch({
                    type: 'artifact_error',
                    error: 'Artifact data is missing or incomplete',
                  });
                  return;
                }
                const contentType = ref.contentType.startsWith('image/') ? 'image' as const
                  : ref.contentType === 'text/html' ? 'html' as const
                  : ref.contentType === 'text/markdown' ? 'markdown' as const
                  : 'text' as const;
                dispatch({
                  type: 'show_artifact',
                  artifact: {
                    title: (data.title as string) ?? ref.filename ?? 'Artifact',
                    contentType,
                    url: ref.url,
                    mimeType: ref.contentType,
                    filename: ref.filename,
                    artifactId: ref.artifactId,
                  },
                });
              })
              .catch((err) => {
                dispatch({
                  type: 'artifact_error',
                  error: err instanceof Error ? err.message : 'Failed to load artifact',
                });
              });
          } else if (data.contentType) {
            // Inline artifact with contentType but no content/url
            if (import.meta.env.DEV) console.log('[artifact-dispatch] show_artifact: inline with contentType only');
            dispatch({ type: 'show_artifact', artifact: data as unknown as Artifact });
          } else {
            // Legacy inline artifact or malformed data — still try to render
            if (import.meta.env.DEV) console.log('[artifact-dispatch] show_artifact: legacy/malformed path');
            dispatch({ type: 'show_artifact', artifact: data as unknown as Artifact });
          }
          break;
        }
        case 'notify': {
          const { message, level } = event.data as {
            message: string;
            level: 'info' | 'success' | 'warning' | 'error';
          };
          toast.showToast(message, level);
          break;
        }
        case 'clear_canvas':
          dispatch({ type: 'clear_canvas' });
          break;
        default:
          // Unknown event type — ignore for forward compatibility
          break;
      }
    },
    [queryClient, toast],
  );

  // Expose dispatch for e2e testing and debugging in dev mode
  useEffect(() => {
    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>).__uiEventContext = {
        dispatchUIEvent,
        getCanvasState: () => canvasState,
      };
    }
  }, [dispatchUIEvent, canvasState]);

  return (
    <UIEventContext.Provider value={{ canvasState, dispatchUIEvent }}>
      {children}
    </UIEventContext.Provider>
  );
}

export function useUIEvent(): UIEventContextValue {
  const context = useContext(UIEventContext);
  if (!context) {
    throw new Error('useUIEvent must be used within a UIEventProvider');
  }
  return context;
}
